//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// === STYLES ===
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PauseSvg from '../../resource/Audio_Audio-13.svg';

// === COMPONENTS ===
import { LoginForm      } from './LoginForm';
import { PassForgotForm } from './PassForgotForm';
import { PassResetForm  } from './PassResetForm';
import { RequestForm    } from './RequestForm';

//#endregion

const VID_TIMEOUT_INTERVAL = 10000;
const VID_TIMEOUT = 120000;

//-----------------------------------------------------------------
export function Login(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const vidRef = useRef(null);

  const [ isVidPlay   , setIsVidPlay   ] = useState( true       );
  const [ vidStart    , setVidStart    ] = useState( Date.now() );

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      backgroundColor : 'black'  ,
      display         : 'flex'   ,
      justifyContent  : 'center' ,
      alignItems      : 'center' ,
      width           : '100vw'  ,
      height          : '100vh'  ,
    },
    form: {
      backgroundColor : theme.palette.background.paper,
      width           : '27rem'  ,
      justifyContent  : 'center' ,
      alignItems      : 'center' ,
      textAlign       : 'center' ,
      opacity         : '0.85'   ,
      zIndex          : '5'      ,
      borderRadius    : '4px'    ,
      padding         : '2.5rem 1.5rem',
    },
    aicloud: {
      marginTop : '1rem'   ,
      fontSize  : '1.5rem' ,
    },
    logo: {      
      margin : '0.5rem' ,
      width  : '60%'    ,
    },
    icon_pause: {
      margin   : 0,
      width    : '2.8rem',
      height   : '2.8rem',
      display  : 'inline-block',
      position : 'fixed',
      right    : '1rem',
      bottom   : 'max(calc(100vh - 850px) / 2 + 1rem, 1rem)',
      cursor   : 'pointer',
      zIndex   : 5,
      '&:hover': {
        background: 'rgb(116, 116, 116)',
      },
      
      mask       : `url(${PauseSvg})` ,
      maskSize   : 'cover'            ,
      background : 'rgb(70, 70, 70)'  ,
    },
    gradient: {
      backgroundImage: `radial-gradient(
        circle at 50% 45%,
        rgba(255, 255, 255, 0.75) 5%,
        rgba(28, 119, 161, 0.5) 45%,
        rgba(0, 0, 0, 0) 90%
      )`,
      width     : '100%',
      height    : '100%',
      maxHeight : '850px',
      objectFit : 'cover',
      position  : 'absolute',
      zIndex    : 2,
    },
    video: {
      width     : '100%',
      height    : '100%',
      maxHeight : '850px',
      objectFit : 'cover',
      position  : 'absolute',
      zIndex    : 0,
    },
  }

  const _props = {    
    AI_CLOUD: {
      sx: _styles.aicloud,
      children: ['AI-CLOUD'],
    },
    LOGO: {
      component : 'img',
      alt       : 'Paradatec Logo',
      sx        : _styles.logo,
      src       : process.env.PUBLIC_URL + '/Logo.svg'
    },
    ICON_PAUSE: {
      style   : _styles.icon_pause,
      onClick : toggleVideo,
    },
    GRADIENT: {
      style   : _styles.gradient,
    },
    VIDEO: {
      style    : _styles.video,
      ref      : vidRef,
      autoPlay : true,
      muted    : true,
      loop     : true,
    },
    VIDEO_SOURCE: {
      src  : process.env.PUBLIC_URL + '/1018909156-sd.mov',
      type :'video/mp4',
    }
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    const vidInterval = setInterval(vidTimeout, VID_TIMEOUT_INTERVAL);

    return function cleanup() {
      clearInterval(vidInterval);
    };
  }, [isVidPlay]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function vidPlay() {
    //-----------------------------------------------------------------
    vidRef.current.play();
    setVidStart(Date.now());
    setIsVidPlay(true);
  }

  //-----------------------------------------------------------------
  function vidPause() {
    //-----------------------------------------------------------------
    vidRef.current.pause();
    setVidStart(null);
    setIsVidPlay(false);
  }

  //-----------------------------------------------------------------
  function vidTimeout() {
    //-----------------------------------------------------------------
    let vidCurr = Date.now();
    if (vidStart && vidCurr - vidStart > VID_TIMEOUT) vidPause();
  }

  //-----------------------------------------------------------------
  async function toggleVideo() {
    //-----------------------------------------------------------------
    isVidPlay ? vidPause() : vidPlay();
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box sx={ _styles.container }>
        <span { ..._props.ICON_PAUSE } />
        <div { ..._props.GRADIENT } />
        <video { ..._props.VIDEO } >
          <source { ..._props.VIDEO_SOURCE } />
        </video>
        <Box sx={ _styles.form }>
          <Box { ..._props.LOGO    } />
          <Box { ..._props.AI_CLOUD } />
          <Routes>
            <Route index                 element={ <LoginForm      />} />
            <Route path="forgot"         element={ <PassForgotForm />} />
            <Route path="reset"          element={ <PassResetForm  />} />
            <Route path="request-access" element={ <RequestForm    />} />
            <Route path='/*'             element={ <Navigate to='.' replace />} />
          </Routes>
        </Box>
      </Box>
    );
  }

  //#endregion

  return display();
}
